import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile, Review } from '../types';
import { 
  MessageSquare, 
  Star, 
  Loader2, 
  TrendingUp, 
  ShieldCheck, 
  Info,
  ChevronDown,
  User,
  Filter,
  SortAsc,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { useTranslation } from '../lib/translations';

interface ReviewsProps {
  profile: UserProfile | null;
}

const Reviews: React.FC<ReviewsProps> = ({ profile }) => {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'highest' | 'lowest'>('newest');
  const [ratingFilter, setRatingFilter] = useState<number | 'all'>('all');

  const locale = language === 'fr' ? fr : enUS;

  useEffect(() => {
    const fetchReviews = async () => {
      if (!profile) return;
      setLoading(true);
      try {
        const q = query(
          collection(db, 'reviews'), 
          where('teacherId', '==', profile.uid),
          orderBy('updatedAt', 'desc')
        );
        const snap = await getDocs(q);
        setReviews(snap.docs.map(d => ({ ...d.data(), id: d.id } as Review)));
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchReviews();
  }, [profile]);

  const avgRating = useMemo(() => {
    if (reviews.length === 0) return null;
    return (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1);
  }, [reviews]);

  const processedReviews = useMemo(() => {
    let result = [...reviews];

    if (ratingFilter !== 'all') {
      result = result.filter(r => r.rating === ratingFilter);
    }

    result.sort((a, b) => {
      if (sortBy === 'newest') return b.updatedAt.toMillis() - a.updatedAt.toMillis();
      if (sortBy === 'oldest') return a.updatedAt.toMillis() - b.updatedAt.toMillis();
      if (sortBy === 'highest') return b.rating - a.rating;
      if (sortBy === 'lowest') return a.rating - b.rating;
      return 0;
    });

    return result;
  }, [reviews, sortBy, ratingFilter]);

  if (loading) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-purple-600" size={48} />
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t.common.loading}</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-10 pb-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-500/20 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase tracking-widest shadow-sm mb-4">
            <TrendingUp size={12} className="animate-pulse" /> {t.reviews.title}
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter">{t.reviews.title}</h1>
          <p className="text-slate-500 font-medium">{t.reviews.desc}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Rating Stats Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 shadow-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
               <Star size={100} />
            </div>
            <div className="relative z-10 text-center">
               <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">{t.reviews.avgRating}</p>
               <div className="flex items-center justify-center gap-4 mb-2">
                 <span className="text-6xl font-black text-slate-900 dark:text-white tracking-tighter">
                   {avgRating || "N/A"}
                 </span>
                 <div className="flex flex-col items-start">
                    <div className="flex text-amber-400">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={20} fill={avgRating && s <= Math.round(parseFloat(avgRating)) ? 'currentColor' : 'none'} strokeWidth={2.5} />
                      ))}
                    </div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                      {reviews.length} {t.reviews.totalReviews}
                    </p>
                 </div>
               </div>
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 p-6 rounded-2xl flex gap-4 items-start shadow-inner">
             <ShieldCheck size={24} className="text-purple-600 shrink-0 mt-1" />
             <div>
                <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-1">{t.reviews.reputationShield}</h4>
                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                  {t.reviews.anonymousDesc}
                </p>
             </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex flex-col sm:flex-row gap-4">
             <div className="relative flex-1 group">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={16} />
                <select 
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl py-3 pl-12 pr-4 text-xs font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-purple-500/10 appearance-none cursor-pointer"
                >
                  <option value="all">{t.reviews.filterAll}</option>
                  {[5, 4, 3, 2, 1].map(s => (
                    <option key={s} value={s}>{t.reviews.ratingLabel.replace('{count}', s.toString())}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
             </div>

             <div className="relative flex-1 group">
                <SortAsc className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={16} />
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl py-3 pl-12 pr-4 text-xs font-black uppercase tracking-widest outline-none focus:ring-4 focus:ring-purple-500/10 appearance-none cursor-pointer"
                >
                  <option value="newest">{t.reviews.sortByNewest}</option>
                  <option value="oldest">{t.reviews.sortByOldest}</option>
                  <option value="highest">{t.reviews.sortByHighest}</option>
                  <option value="lowest">{t.reviews.sortByLowest}</option>
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
             </div>
          </div>

          <div className="space-y-4">
            {processedReviews.length === 0 ? (
              <div className="py-20 text-center border-2 border-dashed border-slate-100 dark:border-white/5 rounded-3xl">
                <MessageSquare size={48} className="text-slate-200 dark:text-slate-800 mx-auto mb-4" />
                <p className="text-slate-400 font-bold italic">{t.reviews.noReviews}</p>
              </div>
            ) : (
              processedReviews.map((rev, i) => (
                <div key={rev.id || i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-2xl p-8 shadow-sm transition-all hover:border-purple-500/20 group">
                   <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div className="flex items-center gap-4">
                         <div className="w-10 h-10 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center text-slate-400 border border-slate-100 dark:border-white/5 group-hover:text-purple-600 transition-colors">
                            <User size={20} />
                         </div>
                         <div>
                            <p className="text-sm font-black text-slate-900 dark:text-white leading-none mb-1">{rev.studentName}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                              <Clock size={10} /> {format(rev.updatedAt.toDate(), 'PPP', { locale })}
                            </p>
                         </div>
                      </div>
                      <div className="flex gap-1 p-2 bg-slate-50 dark:bg-slate-950 rounded-lg">
                         {[1, 2, 3, 4, 5].map(s => (
                           <Star key={s} size={14} fill={s <= rev.rating ? 'currentColor' : 'none'} className={s <= rev.rating ? 'text-amber-400' : 'text-slate-200 dark:text-slate-800'} />
                         ))}
                      </div>
                   </div>
                   <p className="text-slate-600 dark:text-slate-400 text-sm font-medium leading-relaxed italic border-l-4 border-slate-100 dark:border-white/10 pl-6 group-hover:border-purple-600/30 transition-all">
                     "{rev.comment}"
                   </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
